import type { TFunction } from 'i18next';

import type { VestiaireUrlOmission } from '@/features/productSearch/vestiaire/vestiaireUrl';
import { GraphQLResponseError } from '@/shared/lib/graphqlResponseError';

// ============================================================================
// Libellés de l'import d'URL Vestiaire Collective et de l'aperçu d'URL : parties de l'URL non
// reprises (`ignored` de parseVestiaireSearchUrl, clés : docs/superpowers/api-changelog/
// 2026-10-01-vestiaire-import-url.md § 3) et filtres absents de l'URL générée (`omitted`).
// Ne nomment QUE les filtres ou parties concernés (jamais le pourquoi côté serveur).
// ============================================================================

export interface VestiaireUrlIgnoredPart {
  key: string;
  value: string;
}

/** Champs du critère dont un id inconnu est signalé avec sa valeur (« Marques n° 999 »). */
type UnknownIdKey =
  | 'universeIds'
  | 'catalogRootIds'
  | 'catalogLevel1Ids'
  | 'catalogLevel2Ids'
  | 'brandIds'
  | 'modelIds'
  | 'conditionIds'
  | 'colorIds'
  | 'materialIds'
  | 'country';

const UNKNOWN_ID_FIELD_KEYS: Record<UnknownIdKey, string> = {
  universeIds: 'vestiaireSearch.categories',
  catalogRootIds: 'vestiaireSearch.categories',
  catalogLevel1Ids: 'vestiaireSearch.categories',
  catalogLevel2Ids: 'vestiaireSearch.categories',
  brandIds: 'vestiaireSearch.brands',
  modelIds: 'vestiaireSearch.models',
  conditionIds: 'vestiaireSearch.condition',
  colorIds: 'vestiaireSearch.color',
  materialIds: 'vestiaireSearch.material',
  country: 'vestiaireSearch.country',
};

/** Clés du fragment VC reconnues mais à la valeur inutilisable : le libellé du filtre suffit. */
type FragmentKey =
  | 'gender'
  | 'categoryParent'
  | 'category'
  | 'subcategory'
  | 'brand'
  | 'model'
  | 'condition'
  | 'color'
  | 'materialParent'
  | 'material'
  | 'priceMin'
  | 'priceMax'
  | 'sellerBadge'
  | 'editorPicks';

const FRAGMENT_FIELD_KEYS: Record<FragmentKey, string> = {
  gender: 'vestiaireSearch.categories',
  categoryParent: 'vestiaireSearch.categories',
  category: 'vestiaireSearch.categories',
  subcategory: 'vestiaireSearch.categories',
  brand: 'vestiaireSearch.brands',
  model: 'vestiaireSearch.models',
  condition: 'vestiaireSearch.condition',
  color: 'vestiaireSearch.color',
  materialParent: 'vestiaireSearch.material',
  material: 'vestiaireSearch.material',
  priceMin: 'segment.price',
  priceMax: 'segment.price',
  sellerBadge: 'vestiaireSearch.seller',
  editorPicks: 'vestiaireSearch.itemBadge.weLove',
};

const OMISSION_KEYS: Record<VestiaireUrlOmission, string> = {
  sizes: 'vestiaireSearch.size',
  excludedBrands: 'searchModal.excludedBrands',
  excludedKeywords: 'searchModal.excludedKeywords',
  keywordsOr: 'searchModal.keywordsOr',
  vintage: 'vestiaireSearch.itemBadge.vintage',
  exceptional: 'vestiaireSearch.itemBadge.exceptional',
  materials: 'vestiaireSearch.material',
  countries: 'vestiaireSearch.country',
  mixedCategoryLevels: 'vestiaireSearch.mixedCategoryLevels',
};

// Clés du site sans équivalent dans nos filtres mais nommées pour l'utilisateur (les autres restent brutes).
const SITE_ONLY_KEYS: Readonly<Record<string, string>> = {
  sortBy: 'vestiaireSearch.urlPart.sortBy',
};

// Familles de tailles VC (`size0`, `size1`…) et livraison directe : une seule mention chacune.
const SIZE_KEY = /^size\d+$/;
const DIRECT_SHIPPING_KEYS: ReadonlySet<string> = new Set(['dsEligible', 'dsEligibleCountries', 'localCountries']);

// `country` vient de deux sources : un code à 2 lettres sans pays connu (résolution, valeur utile à
// nommer) ou une entrée du fragment sans code exploitable (le libellé seul suffit).
const COUNTRY_CODE = /^[A-Za-z]{2}$/;
// Ids inconnus d'un même champ : au plus 10 nommés, la suite résumée par « … ».
const MAX_LISTED_IDS = 10;

const isUnknownIdKey = (key: string): key is UnknownIdKey => Object.hasOwn(UNKNOWN_ID_FIELD_KEYS, key);

/** Champ de l'id inconnu porté par la partie, ou null si la partie n'est pas un id inconnu. */
const unknownIdField = (part: VestiaireUrlIgnoredPart): UnknownIdKey | null => {
  if (!isUnknownIdKey(part.key)) return null;
  if (part.key === 'country' && !COUNTRY_CODE.test(part.value)) return null;
  return part.key;
};
const isFragmentKey = (key: string): key is FragmentKey => Object.hasOwn(FRAGMENT_FIELD_KEYS, key);

export function ignoredPartLabel(part: VestiaireUrlIgnoredPart, t: TFunction): string {
  if (part.key === 'path') return t('vestiaireSearch.urlPart.path', { value: part.value });
  if (SIZE_KEY.test(part.key)) return t('vestiaireSearch.urlPart.size');
  if (DIRECT_SHIPPING_KEYS.has(part.key)) return t('vestiaireSearch.urlPart.directShipping');
  const idField = unknownIdField(part);
  if (idField) {
    return t('vestiaireSearch.urlPart.unknownId', { field: t(UNKNOWN_ID_FIELD_KEYS[idField]), value: part.value });
  }
  if (part.key === 'country') return t(UNKNOWN_ID_FIELD_KEYS.country);
  if (isFragmentKey(part.key)) return t(FRAGMENT_FIELD_KEYS[part.key]);
  if (Object.hasOwn(SITE_ONLY_KEYS, part.key)) return t(SITE_ONLY_KEYS[part.key]);
  // Autre clé du site sans équivalent dans nos filtres : nommée telle quelle.
  return part.key;
}

const joinUnique = (labels: string[]): string => [...new Set(labels)].join(', ');

/**
 * Libellés des parties non reprises, doublons fusionnés, joints par « , ». Les ids inconnus sont
 * regroupés par libellé de champ (« Marques n° 1, 2 ») à la place de leur première occurrence ; les
 * champs de catégories partagent un même groupe.
 */
export function ignoredPartsText(parts: readonly VestiaireUrlIgnoredPart[], t: TFunction): string {
  const entries: ({ label: string } | { fieldKey: string })[] = [];
  const idsByField = new Map<string, string[]>();
  for (const part of parts) {
    const idField = unknownIdField(part);
    if (!idField) {
      entries.push({ label: ignoredPartLabel(part, t) });
      continue;
    }
    const fieldKey = UNKNOWN_ID_FIELD_KEYS[idField];
    const ids = idsByField.get(fieldKey);
    if (!ids) {
      idsByField.set(fieldKey, [part.value]);
      entries.push({ fieldKey });
    } else if (!ids.includes(part.value)) {
      ids.push(part.value);
    }
  }
  return joinUnique(
    entries.map((entry) => {
      if ('label' in entry) return entry.label;
      const ids = idsByField.get(entry.fieldKey) ?? [];
      const listed = ids.slice(0, MAX_LISTED_IDS).join(', ');
      return t('vestiaireSearch.urlPart.unknownId', {
        field: t(entry.fieldKey),
        value: ids.length > MAX_LISTED_IDS ? `${listed}…` : listed,
      });
    }),
  );
}

export function omissionLabel(omission: VestiaireUrlOmission, t: TFunction): string {
  return t(OMISSION_KEYS[omission]);
}

export function omissionsText(omissions: readonly VestiaireUrlOmission[], t: TFunction): string {
  return joinUnique(omissions.map((omission) => omissionLabel(omission, t)));
}

/** Nombre de champs renseignés d'un critère importé (liste non vide ou nombre présent). */
export function countFilledCriteria(criteria: object): number {
  return Object.values(criteria).filter((value) =>
    Array.isArray(value) ? value.length > 0 : typeof value === 'number',
  ).length;
}

/** Longueur maximale d'une URL acceptée par le serveur (`MAX_VESTIAIRE_URL_LENGTH` côté API). */
export const VESTIAIRE_URL_MAX_LENGTH = 16_384;

// Refus connus du serveur (BadRequest de parseVestiaireSearchUrl, rédigés en français) reconnus par
// leur début stable, puis traduits dans la langue du compte.
const URL_ERROR_PREFIXES: readonly [string, string][] = [
  ['URL illisible', 'vestiaireSearch.urlError.unreadable'],
  ["Cette URL n'est pas une recherche Vestiaire Collective", 'vestiaireSearch.urlError.notVestiaire'],
  ['URL trop longue', 'vestiaireSearch.urlError.tooLong'],
];

/** URL refusée par le serveur (BadRequest connu) : son message traduit ; tout autre échec : générique. */
export function urlImportErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof GraphQLResponseError && error.code === 'BAD_REQUEST') {
    const known = URL_ERROR_PREFIXES.find(([prefix]) => error.message.startsWith(prefix));
    if (known?.[1] === 'vestiaireSearch.urlError.tooLong') return t(known[1], { max: VESTIAIRE_URL_MAX_LENGTH });
    if (known) return t(known[1]);
  }
  return t('searchModal.urlReadError');
}

/** URL saisie mais pas (ou plus) celle déjà importée : à importer avant l'envoi, comme Vinted. */
export function isUrlImportPending(url: string, importedUrl: string | null): boolean {
  const trimmed = url.trim();
  return trimmed !== '' && trimmed !== importedUrl;
}
